import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { toast } from "sonner";
import { Activity, Radio, RefreshCw, Loader2, CheckCircle2, XCircle, Cloud, FileCode, Cpu, Database } from "lucide-react";

function StatCard({ label, value, accent }) {
  return (
    <div className="border border-zinc-800 rounded-sm bg-zinc-900/40 p-4">
      <p className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</p>
      <p className={`font-mono text-2xl mt-1 ${accent || "text-white"}`}>{value}</p>
    </div>
  );
}

function IntegrationRow({ icon: Icon, name, ok, detail, extra }) {
  return (
    <div className="flex items-center gap-3 py-2.5 px-3 border-b border-zinc-800/60 last:border-0">
      <Icon className="h-4 w-4 text-zinc-500" />
      <span className="text-sm text-zinc-200 w-40">{name}</span>
      {ok ? <CheckCircle2 className="h-4 w-4 text-emerald-500" /> : <XCircle className="h-4 w-4 text-red-500" />}
      <span className="text-[11px] font-mono text-zinc-500 flex-1 truncate">{detail}</span>
      {extra && <span className="text-[10px] font-mono text-zinc-600">{extra}</span>}
    </div>
  );
}

export default function OpsHealth() {
  const qc = useQueryClient();
  const { data: h } = useQuery({ queryKey: ["ops-health"], queryFn: async () => (await api.get("/ops/health")).data, refetchInterval: 10000 });
  const { data: integ, isLoading: iLoading } = useQuery({ queryKey: ["ops-integrations"], queryFn: async () => (await api.get("/ops/integrations")).data, refetchInterval: 30000 });

  const pollNow = useMutation({
    mutationFn: async () => (await api.post("/ops/poll-now")).data,
    onSuccess: () => { toast.success("Manual poll executed"); qc.invalidateQueries({ queryKey: ["ops-health"] }); },
    onError: (e) => toast.error(e.response?.data?.detail || "Poll failed (admin only)"),
  });

  return (
    <div className="animate-fade-in">
      <header className="h-14 border-b border-zinc-800 flex items-center px-6 gap-3">
        <h1 className="font-heading text-lg font-semibold tracking-tight">Ops Health</h1>
        <div className="ml-auto">
          <button data-testid="poll-now-btn" onClick={() => pollNow.mutate()} disabled={pollNow.isPending}
            className="flex items-center gap-2 text-sm px-3 py-2 border border-zinc-700 rounded-sm hover:bg-zinc-900 disabled:opacity-50">
            {pollNow.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Poll inbox now
          </button>
        </div>
      </header>

      <div className="p-6 space-y-6">
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Radio className={`h-4 w-4 ${h?.poller_running ? "text-emerald-500" : "text-red-500"}`} />
            <span className={`text-sm font-mono ${h?.poller_running ? "text-emerald-400" : "text-red-400"}`}>
              POLLER {h?.poller_running ? "ONLINE" : "OFFLINE"}
            </span>
            <span className="text-xs font-mono text-zinc-500">interval {h?.poll_interval_seconds}s</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatCard label="Total tickets" value={h?.total_tickets ?? "—"} />
            <StatCard label="Pending jobs" value={h?.pending_jobs ?? "—"} accent={h?.pending_jobs ? "text-amber-400" : "text-white"} />
            <StatCard label="Backoff level" value={h?.backoff ?? 0} accent={h?.backoff ? "text-red-400" : "text-white"} />
            <StatCard label="User approvals" value={h?.pending_user_approvals ?? 0} accent={h?.pending_user_approvals ? "text-amber-400" : "text-white"} />
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="border border-zinc-800 rounded-sm bg-zinc-900/40">
            <div className="flex items-center gap-2 px-3 py-2 border-b border-zinc-800">
              <Activity className="h-3.5 w-3.5 text-zinc-500" /><span className="text-[10px] uppercase tracking-wider text-zinc-400">Poller Heartbeat</span>
            </div>
            <div className="p-3 space-y-1 text-xs font-mono">
              <div className="flex justify-between"><span className="text-zinc-500">Last heartbeat</span><span className="text-zinc-300">{h?.last_heartbeat || "—"}</span></div>
              <div className="flex justify-between"><span className="text-zinc-500">Last poll</span><span className="text-zinc-300">{h?.last_poll || "—"}</span></div>
              <div className="flex justify-between"><span className="text-zinc-500">Last poll ok</span><span className={h?.last_poll_ok ? "text-emerald-400" : "text-red-400"}>{String(h?.last_poll_ok ?? "—")}</span></div>
              {h?.last_error && <div className="text-red-400 mt-2 whitespace-pre-wrap">{h.last_error}</div>}
            </div>
          </div>

          <div className="border border-zinc-800 rounded-sm bg-zinc-900/40">
            <div className="flex items-center gap-2 px-3 py-2 border-b border-zinc-800">
              <Cloud className="h-3.5 w-3.5 text-zinc-500" /><span className="text-[10px] uppercase tracking-wider text-zinc-400">Integrations</span>
            </div>
            {iLoading ? <div className="p-4"><Loader2 className="h-4 w-4 animate-spin text-zinc-500" /></div> : (
              <div>
                <IntegrationRow icon={Cloud} name="Microsoft Graph" ok={integ?.graph?.ok} detail={integ?.graph?.detail} extra={integ?.graph?.mailbox} />
                <IntegrationRow icon={FileCode} name="ZZI eCustoms" ok={integ?.zzi?.ok} detail={integ?.zzi?.detail} extra={integ?.zzi?.safe_mode ? "SAFE MODE" : "LIVE"} />
                <IntegrationRow icon={Cpu} name="LLM (Claude)" ok={integ?.llm?.ok} detail={integ?.llm?.model} />
                <IntegrationRow icon={Database} name="TARIC DB" ok={integ?.taric?.ok} detail={integ?.taric?.source} />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
